import { useState } from "react";
import { Plus, Loader2, AlertTriangle, Upload, Replace, ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { TIPOS_BIEN, TIPO_BIEN_LABEL, POSESION_OPTIONS, POSESION_LABEL, ESTADOS_BIEN, TRANSICIONES_BIEN, fmtMoney } from "@/lib/fideicomisos";
import { toast } from "sonner";

const BIEN_INICIAL = {
  tipo_bien: "", descripcion: "", valor_aportacion: "", valor_avaluo: "",
  fecha_avaluo: "", vigencia_avaluo: "", perito_valuador: "", posesion: "fiduciario",
  avaluo_doc_id: "", escritura_doc_id: "", titulos_doc_id: "", emisor_deudor: "",
  datos_registrales: { folio_real: "", oficina_rpp: "", entidad_federativa: "" },
  inscripcion_rpp: { confirmada: false, folio: "" },
  inscripcion_rug: { confirmada: false, folio: "" },
  convenio_uso_transformacion: { existe: false, doc_id: "" },
  advertencia_406: { notificada: false, doc_id: "", fecha: "" },
};

function DocUpload({ fideicomisoId, tipoCanonico, etiqueta, docId, onUploaded, testid }) {
  const [busy, setBusy] = useState(false);
  const subir = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", etiqueta);
      fd.append("clasificacion", "Fideicomisos");
      fd.append("categoria", "fideicomiso");
      fd.append("referencia_id", fideicomisoId);
      const { data } = await api.post("/documentos/upload", fd);
      await api.post(`/fideicomisos/${fideicomisoId}/documentos`, { documento_id: data.id, tipo_canonico: tipoCanonico });
      onUploaded(data.id, data.nombre_archivo);
      toast.success(`${etiqueta} cargado (SHA-256 ${(data.hash_sha256 || "").slice(0, 10)}…)`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <label className="inline-flex cursor-pointer items-center gap-1.5">
      <input type="file" accept="application/pdf" className="hidden" data-testid={testid}
        onChange={(e) => subir(e.target.files?.[0])} />
      <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-medium ${docId ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
        {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Upload className="mr-1 h-3 w-3" />}
        {docId ? "PDF cargado ✓" : `Cargar ${etiqueta} (PDF)`}
      </span>
    </label>
  );
}

function BienForm({ form, setForm, fideicomisoId, prefix }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setObj = (k, sub, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [sub]: v } }));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Tipo de bien *</label>
          <Select value={form.tipo_bien} onValueChange={(v) => set("tipo_bien", v)}>
            <SelectTrigger data-testid={`${prefix}-tipo`}><SelectValue placeholder="Selecciona" /></SelectTrigger>
            <SelectContent>{TIPOS_BIEN.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Posesión del bien *</label>
          <Select value={form.posesion} onValueChange={(v) => set("posesion", v)}>
            <SelectTrigger data-testid={`${prefix}-posesion`}><SelectValue /></SelectTrigger>
            <SelectContent>{POSESION_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-600">Descripción *</label>
        <Textarea data-testid={`${prefix}-descripcion`} rows={2} value={form.descripcion}
          onChange={(e) => set("descripcion", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Valor de aportación</label>
          <Input data-testid={`${prefix}-valor-aportacion`} type="number" min="0" value={form.valor_aportacion}
            onChange={(e) => set("valor_aportacion", e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Valor de avalúo</label>
          <Input data-testid={`${prefix}-valor-avaluo`} type="number" min="0" value={form.valor_avaluo}
            onChange={(e) => set("valor_avaluo", e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Fecha de avalúo</label>
          <Input data-testid={`${prefix}-fecha-avaluo`} type="date" value={form.fecha_avaluo}
            onChange={(e) => set("fecha_avaluo", e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Vigencia del avalúo</label>
          <Input data-testid={`${prefix}-vigencia-avaluo`} type="date" value={form.vigencia_avaluo}
            onChange={(e) => set("vigencia_avaluo", e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Perito valuador</label>
          <Input data-testid={`${prefix}-perito`} value={form.perito_valuador}
            onChange={(e) => set("perito_valuador", e.target.value)} />
        </div>
        <div className="flex items-end pb-1">
          <DocUpload fideicomisoId={fideicomisoId} tipoCanonico="avaluo_bien" etiqueta="Avalúo"
            docId={form.avaluo_doc_id} testid={`${prefix}-upload-avaluo`}
            onUploaded={(id) => set("avaluo_doc_id", id)} />
        </div>
      </div>

      {form.tipo_bien === "inmueble" && (
        <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Inmueble — datos registrales (RPP)</p>
          <div className="grid grid-cols-3 gap-3">
            <Input data-testid={`${prefix}-folio-real`} placeholder="Folio real" value={form.datos_registrales.folio_real}
              onChange={(e) => setObj("datos_registrales", "folio_real", e.target.value)} />
            <Input placeholder="Oficina RPP" value={form.datos_registrales.oficina_rpp}
              onChange={(e) => setObj("datos_registrales", "oficina_rpp", e.target.value)} />
            <Input placeholder="Entidad federativa" value={form.datos_registrales.entidad_federativa}
              onChange={(e) => setObj("datos_registrales", "entidad_federativa", e.target.value)} />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <DocUpload fideicomisoId={fideicomisoId} tipoCanonico="escritura_publica" etiqueta="Escritura pública"
              docId={form.escritura_doc_id} testid={`${prefix}-upload-escritura`}
              onUploaded={(id) => set("escritura_doc_id", id)} />
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <Checkbox data-testid={`${prefix}-rpp-confirmada`} checked={form.inscripcion_rpp.confirmada}
                onCheckedChange={(v) => setObj("inscripcion_rpp", "confirmada", !!v)} />
              Inscripción RPP confirmada
            </label>
            {form.inscripcion_rpp.confirmada && (
              <Input className="w-40" placeholder="Folio inscripción" value={form.inscripcion_rpp.folio}
                onChange={(e) => setObj("inscripcion_rpp", "folio", e.target.value)} />
            )}
          </div>
          {!form.inscripcion_rpp.confirmada && (
            <p className="flex items-start gap-1.5 text-[11px] text-amber-700">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Sin inscripción en RPP el fideicomiso no surte efectos contra terceros (Art. 388 LGTOC).
            </p>
          )}
        </div>
      )}

      {form.tipo_bien === "mueble" && (
        <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mueble — RUG y convenio de uso</p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <Checkbox checked={form.inscripcion_rug.confirmada}
                onCheckedChange={(v) => setObj("inscripcion_rug", "confirmada", !!v)} />
              Inscripción RUG confirmada
            </label>
            <Input className="w-40" placeholder="Folio RUG" value={form.inscripcion_rug.folio}
              onChange={(e) => setObj("inscripcion_rug", "folio", e.target.value)} />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <Checkbox checked={form.convenio_uso_transformacion.existe}
                onCheckedChange={(v) => setObj("convenio_uso_transformacion", "existe", !!v)} />
              Existe convenio de uso/transformación
            </label>
            {form.convenio_uso_transformacion.existe && (
              <DocUpload fideicomisoId={fideicomisoId} tipoCanonico="convenio_uso_transformacion" etiqueta="Convenio"
                docId={form.convenio_uso_transformacion.doc_id} testid={`${prefix}-upload-convenio`}
                onUploaded={(id) => setObj("convenio_uso_transformacion", "doc_id", id)} />
            )}
          </div>
        </div>
      )}

      {(form.tipo_bien === "derechos_credito" || form.tipo_bien === "acciones_partes_sociales") && (
        <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Derechos / acciones</p>
          <Input data-testid={`${prefix}-emisor`} placeholder="Emisor / deudor" value={form.emisor_deudor}
            onChange={(e) => set("emisor_deudor", e.target.value)} />
          <DocUpload fideicomisoId={fideicomisoId} tipoCanonico="titulos_bien" etiqueta="Títulos"
            docId={form.titulos_doc_id} testid={`${prefix}-upload-titulos`}
            onUploaded={(id) => set("titulos_doc_id", id)} />
        </div>
      )}

      {form.posesion !== "fiduciario" && (
        <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-semibold text-amber-800">Advertencia Art. 406 LGTOC (posesión fuera del fiduciario)</p>
          <p className="text-[11px] text-amber-700">
            Debe notificarse POR ESCRITO al depositario que quien teniendo la posesión transmita, grave o
            disminuya el valor del bien incurre en las sanciones del Art. 406 LGTOC. Sin esta advertencia el
            bien no puede integrarse al patrimonio.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <Checkbox data-testid={`${prefix}-adv406`} checked={form.advertencia_406.notificada}
                onCheckedChange={(v) => setObj("advertencia_406", "notificada", !!v)} />
              Advertencia notificada
            </label>
            <Input className="w-40" type="date" value={form.advertencia_406.fecha}
              onChange={(e) => setObj("advertencia_406", "fecha", e.target.value)} />
            <DocUpload fideicomisoId={fideicomisoId} tipoCanonico="advertencia_406" etiqueta="Advertencia 406"
              docId={form.advertencia_406.doc_id} testid={`${prefix}-upload-adv406`}
              onUploaded={(id) => setObj("advertencia_406", "doc_id", id)} />
          </div>
        </div>
      )}
    </div>
  );
}

export function PatrimonioTab({ fid, reload, puedeEscribir }) {
  const terminal = ["extinguido", "revertido"].includes(fid.estado);
  const operable = ["en_integracion", "constituido", "vigente"].includes(fid.estado);
  const bienes = fid.patrimonio || [];
  const [alta, setAlta] = useState(false);
  const [form, setForm] = useState(BIEN_INICIAL);
  const [busy, setBusy] = useState(false);
  const [trans, setTrans] = useState(null); // {bien, nuevo}
  const [motivo, setMotivo] = useState("");
  const [sust, setSust] = useState(null); // bien a sustituir
  const [sustForm, setSustForm] = useState(BIEN_INICIAL);
  const [sustMotivo, setSustMotivo] = useState("");

  const crear = async () => {
    setBusy(true);
    try {
      await api.post(`/fideicomisos/${fid.id}/bienes`, form);
      toast.success("Bien aportado al fideicomiso (estado: aportado).");
      setAlta(false); setForm(BIEN_INICIAL);
      await reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const transicionar = async () => {
    setBusy(true);
    try {
      await api.post(`/fideicomisos/${fid.id}/bienes/${trans.bien.id}/transicion`, { nuevo_estado: trans.nuevo, motivo });
      toast.success(`Bien → ${ESTADOS_BIEN[trans.nuevo]?.label}.`);
      setTrans(null); setMotivo("");
      await reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const sustituir = async () => {
    setBusy(true);
    try {
      await api.post(`/fideicomisos/${fid.id}/bienes/${sust.id}/sustituir`, { motivo: sustMotivo, nuevo_bien: sustForm });
      toast.success("Bien sustituido con trazabilidad completa.");
      setSust(null); setSustForm(BIEN_INICIAL); setSustMotivo("");
      await reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 p-4">
        <div>
          <p className="text-sm font-semibold text-slate-800">Patrimonio fideicomitido</p>
          <p className="text-xs text-slate-500">
            Patrimonio vigente (bienes en patrimonio): <b>{fmtMoney(fid.cobertura?.patrimonio_vigente)}</b>
            {fid.cobertura?.avaluos_vencidos > 0 && (
              <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                {fid.cobertura.avaluos_vencidos} avalúo(s) vencido(s)
              </span>
            )}
          </p>
        </div>
        {puedeEscribir && operable && (
          <Button data-testid="fid-btn-alta-bien" size="sm" onClick={() => setAlta(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> Aportar bien
          </Button>
        )}
      </div>
      {bienes.length === 0 ? (
        <p className="p-8 text-center text-sm text-slate-400">
          Sin bienes registrados. Para constituir el fideicomiso se requiere al menos 1 bien en patrimonio.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="px-4 py-2.5">Bien</th><th className="px-4 py-2.5">Avalúo</th>
              <th className="px-4 py-2.5">Vigencia</th><th className="px-4 py-2.5">Posesión</th>
              <th className="px-4 py-2.5">Estado</th><th className="px-4 py-2.5">Alertas</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {bienes.map((b) => {
              const m = ESTADOS_BIEN[b.estado_bien] || {};
              const transiciones = TRANSICIONES_BIEN[b.estado_bien] || [];
              return (
                <tr key={b.id} className="border-b border-slate-50 align-top" data-testid={`fid-bien-${b.id}`}>
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-slate-800">{TIPO_BIEN_LABEL[b.tipo_bien] || b.tipo_bien}</p>
                    <p className="max-w-[220px] truncate text-xs text-slate-500" title={b.descripcion}>{b.descripcion}</p>
                    {b.sustituido_por_bien_id && <p className="text-[11px] text-blue-600">Sustituido por bien {b.sustituido_por_bien_id.slice(0, 8)}…</p>}
                    {b.sustituye_a_bien_id && <p className="text-[11px] text-blue-600">Sustituye al bien {b.sustituye_a_bien_id.slice(0, 8)}…</p>}
                  </td>
                  <td className="px-4 py-2.5 text-slate-700">{fmtMoney(b.valor_avaluo)}</td>
                  <td className="px-4 py-2.5 text-slate-600">{b.vigencia_avaluo || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{POSESION_LABEL[b.posesion] || b.posesion}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>{m.label || b.estado_bien}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-col gap-1">
                      {b.badges?.avaluo_vencido && <span className="w-fit rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700" data-testid={`fid-bien-avaluo-vencido-${b.id}`}>Avalúo vencido</span>}
                      {b.badges?.rpp_pendiente && <span className="w-fit rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700" title={b.badges.warning_388}>RPP pendiente (Art. 388)</span>}
                      {b.badges?.advertencia_406_pendiente && <span className="w-fit rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Advertencia 406 pendiente</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    {puedeEscribir && !terminal && (
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {transiciones.map((t) => (
                          <Button key={t} data-testid={`fid-bien-trans-${t}-${b.id}`} size="sm" variant="outline"
                            onClick={() => { setTrans({ bien: b, nuevo: t }); setMotivo(""); }}>
                            <ArrowRightLeft className="mr-1 h-3 w-3" /> {ESTADOS_BIEN[t]?.label}
                          </Button>
                        ))}
                        {["aportado", "en_patrimonio"].includes(b.estado_bien) && (
                          <Button data-testid={`fid-bien-sustituir-${b.id}`} size="sm" variant="outline"
                            onClick={() => { setSust(b); setSustForm(BIEN_INICIAL); setSustMotivo(""); }}>
                            <Replace className="mr-1 h-3 w-3" /> Sustituir
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Alta de bien */}
      <Dialog open={alta} onOpenChange={(v) => { if (!v) setAlta(false); }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-xl" data-testid="fid-dialog-bien">
          <DialogHeader><DialogTitle className="font-heading">Aportar bien al fideicomiso</DialogTitle></DialogHeader>
          <BienForm form={form} setForm={setForm} fideicomisoId={fid.id} prefix="fid-bien" />
          <DialogFooter className="sticky bottom-0 bg-white pt-2">
            <Button variant="outline" onClick={() => setAlta(false)}>Cancelar</Button>
            <Button data-testid="fid-bien-crear" onClick={crear} disabled={busy || !form.tipo_bien || !form.descripcion.trim()}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Aportar bien
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transición de bien */}
      <Dialog open={!!trans} onOpenChange={(v) => { if (!v) setTrans(null); }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-md" data-testid="fid-dialog-bien-trans">
          <DialogHeader>
            <DialogTitle className="font-heading">Bien → {ESTADOS_BIEN[trans?.nuevo]?.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <label className="mb-1 block text-xs font-medium text-slate-600">Motivo (obligatorio, queda en bitácora) *</label>
            <Textarea data-testid="fid-bien-trans-motivo" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </div>
          <DialogFooter className="sticky bottom-0 bg-white pt-2">
            <Button variant="outline" onClick={() => setTrans(null)}>Cancelar</Button>
            <Button data-testid="fid-bien-trans-confirmar" onClick={transicionar} disabled={busy || !motivo.trim()}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sustitución */}
      <Dialog open={!!sust} onOpenChange={(v) => { if (!v) setSust(null); }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-xl" data-testid="fid-dialog-sustituir">
          <DialogHeader><DialogTitle className="font-heading">Sustituir bien — trazabilidad completa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="rounded-md bg-slate-50 p-2.5 text-[11px] text-slate-500">
              El bien actual pasará a "Sustituido" y quedará ligado al nuevo. El bien sustituto requiere valor de avalúo obligatorio.
            </p>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Motivo de la sustitución *</label>
              <Textarea data-testid="fid-sustituir-motivo" rows={2} value={sustMotivo} onChange={(e) => setSustMotivo(e.target.value)} />
            </div>
            <BienForm form={sustForm} setForm={setSustForm} fideicomisoId={fid.id} prefix="fid-sust" />
          </div>
          <DialogFooter className="sticky bottom-0 bg-white pt-2">
            <Button variant="outline" onClick={() => setSust(null)}>Cancelar</Button>
            <Button data-testid="fid-sustituir-confirmar" onClick={sustituir}
              disabled={busy || !sustMotivo.trim() || !sustForm.tipo_bien || !sustForm.descripcion.trim() || !Number(sustForm.valor_avaluo)}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Sustituir bien
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
